import { hasPermission } from 'core/auth/rbac'
import { ConfirmDialog } from 'core/components/ui/ConfirmDialog'
import { FilterBar } from 'core/components/ui/FilterBar'
import { Modal } from 'core/components/ui/Modal'
import { PageHeader } from 'core/components/ui/PageHeader'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React, { useState } from 'react'
import { paymentRepository } from '../../data/repositories/PaymentRepositoryImpl'
import { PaymentDetailRecord, PaymentRecord, PaymentStatus } from '../../domain/entities/Payment'
import { PaymentDetailModal } from '../components/PaymentDetailModal'
import { PaymentQueue } from '../components/PaymentQueue'
import { RecordOfflinePaymentModal } from '../components/RecordOfflinePaymentModal'
import { usePayments } from '../hooks/usePayments'

export const PaymentsPage: React.FC = () => {
  const {
    payments,
    loading,
    error,
    search,
    setSearch,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    approvePayment,
    refusePayment,
    recordOffline,
    resetFilters,
  } = usePayments(10)

  const { user } = useAuth()
  const canRecordOffline = hasPermission(user, 'record_offline_payments')
  const canApprove = hasPermission(user, 'approve_payments')

  const [selectedRecord, setSelectedRecord] = useState<PaymentDetailRecord | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)

  // Dialogs
  const [approveOpen, setApproveOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [offlineOpen, setOfflineOpen] = useState(false)
  const [actioning, setActioning] = useState(false)

  const handleReviewClick = async (record: PaymentRecord) => {
    setSelectedRecord(null)
    setDetailOpen(true)
    setDetailLoading(true)
    try {
      const detail = await paymentRepository.getPaymentDetail(record.id)
      setSelectedRecord(detail)
    } catch (err) {
      setDetailOpen(false)
    } finally {
      setDetailLoading(false)
    }
  }

  const handleApproveTrigger = () => {
    setDetailOpen(false)
    setApproveOpen(true)
  }

  const handleRejectTrigger = () => {
    setDetailOpen(false)
    setRejectReason('')
    setRejectOpen(true)
  }

  const handleApproveConfirm = async () => {
    if (!selectedRecord) return
    setActioning(true)
    try {
      await approvePayment(selectedRecord.id, selectedRecord.orderId)
      setApproveOpen(false)
      setSelectedRecord(null)
    } catch (err) {
      // Catch
    } finally {
      setActioning(false)
    }
  }

  const handleRejectConfirm = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedRecord || !rejectReason) return
    setActioning(true)
    try {
      await refusePayment(selectedRecord.id, rejectReason)
      setRejectOpen(false)
      setSelectedRecord(null)
    } catch (err) {
      // Catch
    } finally {
      setActioning(false)
    }
  }

  const handleOfflineSubmit = async (
    data: Omit<PaymentRecord, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'buyer'>,
  ) => {
    await recordOffline(data)
  }

  const isFilterActive = !!(search || status !== undefined)

  const rejectModalFooter = (
    <>
      <button
        type="button"
        onClick={() => setRejectOpen(false)}
        disabled={actioning}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="reject-payment-form"
        disabled={actioning || !rejectReason}
        className="px-4 py-2 text-sm font-semibold bg-danger hover:bg-danger-hover text-white rounded-btn transition-colors disabled:opacity-50 min-w-[5rem] flex items-center justify-center"
      >
        {actioning ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          'Confirm Reject'
        )}
      </button>
    </>
  )

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Payments Verification"
        description="Review transaction receipt screenshots uploaded by retailers or log physical cash receipts manually."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Payments' }]}
        actions={
          canRecordOffline && (
            <button
              onClick={() => setOfflineOpen(true)}
              className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-fg rounded-btn shadow transition-all flex items-center gap-2 active:scale-95"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              Record Offline Payment
            </button>
          )
        }
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search by Txn Reference or Payment ID..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={isFilterActive}
        onReset={resetFilters}
      >
        {/* Status Filter */}
        <select
          value={status === undefined ? 'all' : String(status)}
          onChange={(e) => {
            const val = e.target.value
            setStatus(val === 'all' ? undefined : (val as PaymentStatus))
            setPage(1)
          }}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none"
        >
          <option value="uploaded">Pending Review</option>
          <option value="verified">Verified Payments</option>
          <option value="rejected">Rejected Payments</option>
          <option value="all">All Payments</option>
        </select>
      </FilterBar>

      {/* Table Data queue */}
      <PaymentQueue
        payments={payments}
        loading={loading}
        onReview={handleReviewClick}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
      />

      {/* Detail Review Modal */}
      <PaymentDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        record={selectedRecord}
        loading={detailLoading}
        onApprove={handleApproveTrigger}
        onReject={handleRejectTrigger}
        readOnly={selectedRecord?.status !== 'uploaded' || !canApprove}
      />

      {/* Approve Confirm Dialog */}
      <ConfirmDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={handleApproveConfirm}
        title="Approve Transaction Receipt"
        message={`Are you sure you want to verify and confirm receipt of payment for order ${selectedRecord?.orderId}? This will automatically advance the order status to Payment Verified.`}
        confirmText="Confirm & Verify"
        tone="primary"
        loading={actioning}
      />

      {/* Reject Reason Form Modal */}
      <Modal
        isOpen={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Reject Transaction Receipt"
        footer={rejectModalFooter}
        size="sm"
      >
        <form id="reject-payment-form" onSubmit={handleRejectConfirm} className="space-y-4">
          <div className="space-y-2">
            <p className="text-text-secondary text-sm">
              Please input the rejection reason. This message will be sent directly to the retailer&apos;s app.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Reference number doesn't match banking logs / screenshot is illegible..."
              required
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-danger focus:outline-none transition-colors"
            />
          </div>
        </form>
      </Modal>

      {/* Record Offline Payment Modal */}
      <RecordOfflinePaymentModal
        isOpen={offlineOpen}
        onClose={() => setOfflineOpen(false)}
        onSubmit={handleOfflineSubmit}
      />
    </div>
  )
}
